// services/dataService.js
import { SENSOR_NAME_MAP, SENSOR_FIELDS } from '../constants/sensors'
import { listAvailableSensors, listAvailableFields } from './influxService'

/**
 * Calculate per-buoy availability using actual data from InfluxDB
 * @param {Array} selectedBuoys - Array of selected buoy IDs
 * @param {Object} available - Available data from useSensorData hook
 * @param {string} timeRange - Time range for data availability
 * @returns {Object} Per-buoy sensor and field availability
 */
export async function calculatePerBuoyAvailability(selectedBuoys, available, timeRange = '-7d') {
  const byBuoy = {}
  
  if (!selectedBuoys.length) return byBuoy
  
  try {
    // Get actual sensor availability from InfluxDB
    const availableSensors = await listAvailableSensors(selectedBuoys, timeRange)
    
    // Get all unique sensors across selected buoys
    const allSensors = new Set()
    Object.values(availableSensors).forEach(sensors => {
      sensors.forEach(sensor => allSensors.add(sensor))
    })
    
    // Get field availability for all sensors
    const availableFields = await listAvailableFields(selectedBuoys, Array.from(allSensors), timeRange)
    
    for (const buoyId of selectedBuoys) {
      const sensors = availableSensors[buoyId] || []
      const fieldsBySensor = availableFields[buoyId] || {}
      
      byBuoy[buoyId] = {
        availableSensors: sensors,
        availableFieldsBySensor: fieldsBySensor
      }
    }
  } catch (error) {
    console.error('Error calculating per-buoy availability:', error)
    // Return empty structure on error
    for (const buoyId of selectedBuoys) {
      byBuoy[buoyId] = {
        availableSensors: [],
        availableFieldsBySensor: {}
      }
    }
  }
  
  return byBuoy
}

/**
 * Synchronous version for immediate feedback (returns empty structure)
 * @param {Array} selectedBuoys - Array of selected buoy IDs
 * @param {Object} available - Available data from useSensorData hook
 * @returns {Object} Per-buoy sensor and field availability
 */
export function calculatePerBuoyAvailabilitySync(selectedBuoys, available) {
  const byBuoy = {}
  
  for (const buoyId of selectedBuoys) {
    byBuoy[buoyId] = {
      availableSensors: available.sensors || [],
      availableFieldsBySensor: {}
    }
    
    // Convert Map to object if needed
    if (available.fieldsBySensor instanceof Map) {
      for (const [sensor, fields] of available.fieldsBySensor.entries()) {
        byBuoy[buoyId].availableFieldsBySensor[sensor] = Array.from(fields)
      }
    }
  }
  
  return byBuoy
}

/**
 * Filter buoy-specific data from raw data
 * @param {Array} rawData - Raw sensor data
 * @param {string} buoyId - Buoy identifier
 * @returns {Array} Filtered data for specific buoy
 */
export function filterBuoyData(rawData, buoyId) {
  return rawData.filter(d => (d.buoy_id || d.buoyId) === buoyId)
}

/**
 * Get sensor label from sensor key
 * @param {string} sensor - Sensor key
 * @returns {string} Human-readable sensor name
 */
export function getSensorLabel(sensor) {
  return SENSOR_NAME_MAP?.[sensor] || sensor
}

/**
 * Get field label from sensor and field keys
 * @param {string} sensor - Sensor key
 * @param {string} field - Field key
 * @returns {string} Human-readable field name
 */
export function getFieldLabel(sensor, field) {
  return SENSOR_FIELDS[sensor]?.[field] || field
}
  